/*-------------------------------------------------------
||Program           : VoucherCreate.jsx
||Application       : Voucher
||Option            : Create Voucher
||Developer         : Ashish Kumar
||Date              : 2026-04-28
----------------------------------------------------------*/

import React, { Component } from "react";
import { Container, InputGroup, Form } from "react-bootstrap";
import Select from "react-select";
import DatePicker from "react-datepicker";
import Moment from "moment";
import VoucherService from "../../services/VoucherService";
import Alerts from "../common/Alerts";

export class VoucherCreate extends Component {
  constructor(props) {
    super(props);
    this.expiryDateInputGroupRef = React.createRef();

    this.state = {
      expiryDatePlacement: "bottom-start",
      loading: false,
      isSubmit: false,

      showAlertModal: false,
      alertModalType: "",
      alertModalTitle: "",
      alertModalMessage: "",

      reservationOptions: [],
      selectedReservation: null,

      reservation_id: null,
      voucher_code: "",
      confirmation_code: "",
      guest_name: "",
      guest_email: "",
      amount: 50,
      status: "active",
      expiry_date: Moment().add(1, "year").format("YYYY-MM-DD"),
      created_at: new Date(),
      // Promo code fields — only sent when is_promo_code is true.
      is_promo_code: false,
      max_usage_count: "",
    };

    this.getEligibleReservations = this.getEligibleReservations.bind(this);
    this.handleReservationSelect = this.handleReservationSelect.bind(this);
    this.changeHandler = this.changeHandler.bind(this);
    this.createVoucher = this.createVoucher.bind(this);
  }

  componentDidMount() {
    this.getEligibleReservations();
  }

  triggerAlert(type, title, message) {
    this.setState({
      showAlertModal: true,
      alertModalType: type,
      alertModalTitle: title,
      alertModalMessage: message,
    });

    setTimeout(
      () => this.setState({ showAlertModal: false }),
      global.alert_close_time || 3000,
    );
  }

  getGuestName(item = {}) {
    return `${item.guest_first_name || ""} ${item.guest_last_name || ""}`.trim();
  }

  splitGuestName(guestName = "") {
    const trimmedName = guestName.trim();
    if (!trimmedName) {
      return {
        guest_first_name: "",
        guest_last_name: "",
      };
    }

    const nameParts = trimmedName.split(/\s+/);
    if (nameParts.length === 1) {
      return {
        guest_first_name: nameParts[0],
        guest_last_name: "",
      };
    }

    return {
      guest_first_name: nameParts.slice(0, -1).join(" "),
      guest_last_name: nameParts[nameParts.length - 1],
    };
  }

  async getEligibleReservations() {
    this.setState({ loading: true });
    const res = await VoucherService.getEligibleReservationsForVoucher();

    if (global.successStatus.includes(res.status)) {
      const list = res.data?.data ? res.data.data : [];
      const options = list.map((item) => {
        const guestName = this.getGuestName(item);
        const unitName = item.unit_name || "Unit";
        const code = item.confirmation_code || "";
        return {
          value: item.reservation_id,
          label: `${unitName} - ${guestName || "Guest"} - ${code}`,
          raw: item,
        };
      });
      this.setState({ reservationOptions: options });
    } else {
      this.triggerAlert("error", "Error!", res.message || "Unable to load.");
      this.setState({ reservationOptions: [] });
    }

    this.setState({ loading: false });
  }

  handleReservationSelect(value) {
    if (!value) {
      this.setState({
        selectedReservation: null,
        reservation_id: null,
        voucher_code: "",
        confirmation_code: "",
        guest_name: "",
        guest_email: "",
        amount: 50,
        status: "active",
        expiry_date: Moment().add(1, "year").format("YYYY-MM-DD"),
        created_at: new Date(),
      });
      return;
    }

    const item = value.raw || {};
    const code = item.confirmation_code || "";

    this.setState({
      selectedReservation: value,
      reservation_id: item.reservation_id || null,
      voucher_code: code,
      confirmation_code: code,
      guest_name: this.getGuestName(item),
      guest_email: item.guest_email || "",
      amount: 50,
      status: "active",
      expiry_date: Moment().add(1, "year").format("YYYY-MM-DD"),
      created_at: new Date(),
    });
  }

  getResponsivePlacement = (ref) => {
    if (!ref.current) return "bottom-start";

    const rect = ref.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const CALENDAR_HEIGHT_ESTIMATE = 330;
    const CALENDAR_WIDTH_ESTIMATE = 300;

    if (spaceBelow >= CALENDAR_HEIGHT_ESTIMATE || window.innerWidth <= 767) {
      return "bottom-start";
    }

    const spaceRight = window.innerWidth - rect.right;
    const spaceLeft = rect.left;

    if (spaceRight >= CALENDAR_WIDTH_ESTIMATE) return "right-start";
    if (spaceLeft >= CALENDAR_WIDTH_ESTIMATE) return "left-start";
    return spaceRight >= spaceLeft ? "right-start" : "left-start";
  };

  checkExpiryDatePlacement = () => {
    this.setState({
      expiryDatePlacement: this.getResponsivePlacement(this.expiryDateInputGroupRef),
    });
  };

  changeHandler(event) {
    let value =
      event.target.type === "checkbox"
        ? event.target.checked
        : event.target.value;
    this.setState({ [event.target.name]: value });
  }

  async createVoucher() {
    if (!this.state.reservation_id) {
      this.triggerAlert("error", "Validation Error", "Please select a reservation.");
      return;
    }

    // Promo code requires a max usage count of at least 1.
    if (this.state.is_promo_code) {
      const maxCount = parseInt(this.state.max_usage_count, 10);
      if (!this.state.max_usage_count || isNaN(maxCount) || maxCount < 1) {
        this.triggerAlert("error", "Validation Error", "Max Usage Count is required and must be at least 1 for a Promo Code.");
        return;
      }
    }

    this.setState({ isSubmit: true });
    const { guest_first_name, guest_last_name } = this.splitGuestName(
      this.state.guest_name,
    );
    const payload = {
      reservation_id: this.state.reservation_id,
      amount:
        this.state.amount !== undefined && this.state.amount !== null
          ? parseFloat(this.state.amount)
          : 50,
      status: this.state.status,
      expiry_date: this.state.expiry_date ? this.state.expiry_date : null,
      guest_first_name,
      guest_last_name,
      // Send null (not empty string) so the backend nullable rule passes when email is absent.
      guest_email: this.state.guest_email ? this.state.guest_email.trim() : null,
      is_promo_code: this.state.is_promo_code ? 1 : 0,
      max_usage_count: this.state.is_promo_code
        ? parseInt(this.state.max_usage_count, 10)
        : null,
    };

    const res = await VoucherService.manualCreateVoucher(payload);
    if (global.successStatus.includes(res.status)) {
      const created = res.data?.created ? true : false;
      const voucher = res.data?.voucher ? res.data.voucher : null;
      const voucherCode = voucher?.voucher_code || this.state.voucher_code;

      this.triggerAlert(
        "success",
        "Success",
        created ? "Voucher created." : "Voucher already exists.",
      );

      if (voucherCode) {
        setTimeout(() => this.props.history.push(`/vouchers/${voucherCode}`), 600);
      } else {
        setTimeout(() => this.props.history.push(`/vouchers`), 600);
      }
    } else {
      this.triggerAlert("error", "Error!", res.message || "Error!");
    }
    this.setState({ isSubmit: false });
  }

  render() {
    const customStyles = {
      option: (provided, state) => ({
        ...provided,
        color:
          state.isSelected && state.isFocused
            ? "white"
            : state.isSelected
              ? "black"
              : state.isFocused && "white",
        backgroundColor:
          state.isSelected && state.isFocused
            ? "black"
            : state.isFocused && "black",
      }),
    };

    const statusOptions = [
      { value: "active", label: "Active" },
      { value: "used", label: "Used" },
      { value: "expired", label: "Expired" },
    ];
    const selectedStatus =
      statusOptions.find((item) => item.value === this.state.status) ||
      statusOptions[0];

    return (
      <main>
        <Container className="innter-container">
          <div className="d-flex flex-column">
            <div className="row row-xs mg-t-0 pd-t-0 pd-lg-l-15 pd-xs-l-0">
              <div className="col-md-12">
                <div className="col-md-12 mg-t-20 mg-b-10"></div>
                <div className="col-md-12 mg-t-0 mg-b-10 pd-t-0 pd-b-10 pd-l-15 pd-r-0">
                  <div className="dashbaord-pageHeadertitle d-flex justify-content-between align-items-center flex-wrap mg-b-0">
                    <h3>Create Voucher</h3>
                    <div className="d-flex justify-content-end">
                      <button
                        type="button"
                        className="btn-success-outline-small"
                        onClick={() => this.props.history.push("/vouchers")}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        className="btn-success ml-3"
                        onClick={this.createVoucher}
                        disabled={this.state.isSubmit}
                      >
                        {this.state.isSubmit ? global.loader : "Save"}
                      </button>
                    </div>
                  </div>
                </div>

                <div className="scrolling-carousel pd-l-15 pd-r-15">
                  <div className="dashboardRightcard background-none pt-0 pd-l-30 pd-r-30">
                    <div className="col-md-12 pd-l-30 pd-r-30">
                      <Alerts
                        show={this.state.showAlertModal}
                        type={this.state.alertModalType}
                        title={this.state.alertModalTitle}
                        message={this.state.alertModalMessage}
                      />
                    </div>

                    <div className="row mg-b-20">
                      <div className="col-md-4">
                        <InputGroup className="mt-3">
                          <label>Reservation</label>
                          <div className="col-md-12 p-0">
                            <Select
                              styles={customStyles}
                              isClearable={true}
                              className="multiselect"
                              menuPlacement="auto"
                              value={this.state.selectedReservation}
                              options={this.state.reservationOptions}
                              onChange={(value) => this.handleReservationSelect(value)}
                              theme={(theme) => ({
                                ...theme,
                                borderRadius: 2,
                                colors: {
                                  ...theme.colors,
                                  primary: "#fff",
                                  primary75: "#000",
                                  primary50: "#000",
                                  primary25: "#000",
                                },
                              })}
                            />
                          </div>
                        </InputGroup>
                      </div>

                      <div className="col-md-4">
                        <InputGroup className="mt-3">
                          <label>Voucher Code</label>
                          <div className="col-md-12 p-0">
                            <Form.Control type="text" value={this.state.voucher_code} disabled />
                          </div>
                        </InputGroup>
                      </div>
                      <div className="col-md-4">
                        <InputGroup className="mt-3">
                          <label>Confirmation Code</label>
                          <div className="col-md-12 p-0">
                            <Form.Control type="text" value={this.state.confirmation_code} disabled />
                          </div>
                        </InputGroup>
                      </div>
                      <div className="col-md-4">
                        <InputGroup className="mt-3">
                          <label>Guest Name</label>
                          <div className="col-md-12 p-0">
                            <Form.Control
                              type="text"
                              name="guest_name"
                              value={this.state.guest_name}
                              onChange={this.changeHandler}
                              placeholder="Guest name"
                              autoComplete="off"
                            />
                          </div>
                        </InputGroup>
                      </div>
                      <div className="col-md-4">
                        <InputGroup className="mt-3">
                          <label>Guest Email</label>
                          <div className="col-md-12 p-0">
                            <Form.Control
                              type="text"
                              name="guest_email"
                              value={this.state.guest_email}
                              onChange={this.changeHandler}
                              autoComplete="off"
                            />
                          </div>
                        </InputGroup>
                      </div>
                      <div className="col-md-4">
                        <InputGroup className="mt-3">
                          <label>Amount</label>
                          <div className="col-md-12 p-0">
                            <div className="input-group-prepend prefix-dollar">
                              <span className="input-group-text">$</span>
                              <Form.Control
                                type="text"
                                name="amount"
                                step="0.01"
                                min="0"
                                value={this.state.amount}
                                onChange={this.changeHandler}
                              />
                            </div>
                          </div>
                        </InputGroup>
                      </div>
                      <div className="col-md-4">
                        <InputGroup className="mt-3">
                          <label>Status</label>
                          <div className="col-md-12 p-0">
                            <Select
                              styles={customStyles}
                              isClearable={false}
                              className="multiselect"
                              menuPlacement="auto"
                              value={selectedStatus}
                              options={statusOptions}
                              onChange={(value) =>
                                this.setState({
                                  status: value ? value.value : "active",
                                })
                              }
                              theme={(theme) => ({
                                ...theme,
                                borderRadius: 2,
                                colors: {
                                  ...theme.colors,
                                  primary: "#fff",
                                  primary75: "#000",
                                  primary50: "#000",
                                  primary25: "#000",
                                },
                              })}
                            />
                          </div>
                        </InputGroup>
                      </div>
                      <div className="col-md-4">
                        <InputGroup className="mt-3" ref={this.expiryDateInputGroupRef}>
                          <label>Expiry Date</label>
                          <div className="col-md-12 p-0">
                            <DatePicker
                              className="form-control max_width_100"
                              disabledKeyboardNavigation
                              strictParsing
                              scrollableYearDropdown
                              showMonthDropdown
                              showYearDropdown
                              autoComplete="off"
                              yearDropdownItemNumber={global.yearDropdownItemNumber}
                              dateFormat="MM/dd/yy"
                              selected={
                                this.state.expiry_date
                                  ? Moment(this.state.expiry_date, "YYYY-MM-DD").toDate()
                                  : null
                              }
                              onChange={(date) =>
                                this.setState({
                                  expiry_date: date ? Moment(date).format("YYYY-MM-DD") : "",
                                })
                              }
                              popperPlacement={this.state.expiryDatePlacement}
                              onCalendarOpen={this.checkExpiryDatePlacement}
                              popperModifiers={{
                                preventOverflow: {
                                  enabled: true,
                                  escapeWithReference: false,
                                },
                              }}
                            />
                          </div>
                        </InputGroup>
                      </div>
                      <div className="col-md-4">
                        <InputGroup className="mt-3">
                          <label>Created At</label>
                          <div className="col-md-12 p-0">
                            <Form.Control
                              type="text"
                              value={
                                this.state.created_at
                                  ? Moment(this.state.created_at).format(global.dateFormat)
                                  : ""
                              }
                              disabled
                            />
                          </div>
                        </InputGroup>
                      </div>
                      <div className="col-md-4">
                        <div className="mt-3">
                          <label className="form-label text-left pd-b-5">Promo Code</label>
                          <Form.Check
                            name="is_promo_code"
                            type={"checkbox"}
                            id={`is_promo_code`}
                            label=""
                            className={"ckbox"}
                            onChange={this.changeHandler}
                            checked={this.state.is_promo_code}
                          />
                        </div>
                      </div>
                      {this.state.is_promo_code && (
                        <div className="col-md-4">
                          <InputGroup className="mt-3">
                            <label>Max Usage Count <span style={{ color: "red" }}>*</span></label>
                            <div className="col-md-12 p-0">
                              <Form.Control
                                type="number"
                                name="max_usage_count"
                                min="1"
                                step="1"
                                value={this.state.max_usage_count}
                                onChange={this.changeHandler}
                                placeholder="e.g. 100"
                              />
                            </div>
                          </InputGroup>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Container>
      </main>
    );
  }
}

export default VoucherCreate;
